/** Student Portal — contextual copy & data helpers */

export const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

export const getProgress = (enr) =>
  Math.max(0, Math.min(100, enr?.progress?.percentageComplete ?? enr?.progressPercentage ?? 0));

export const getCompletedLessonCount = (enr) => {
  const cc = enr?.progress?.curriculumCompleted;
  if (Array.isArray(cc) && cc.length) return cc.length;
  const cl = enr?.progress?.completedLessons;
  if (!Array.isArray(cl)) return 0;
  return cl.filter((item) => typeof item === 'string' || item?.completed !== false).length;
};

export const getInstructorName = (course) => {
  if (!course) return 'Instructor';
  const inst = course.instructor;
  return (
    inst?.name
    || inst?.id?.user?.name
    || inst?.user?.name
    || inst?.title
    || 'Instructor'
  );
};

export const resolveCourseThumbnail = (course, apiBase = '') => {
  const thumb = course?.thumbnail;
  if (!thumb) return null;
  if (/^https?:\/\//i.test(thumb)) return thumb;
  if (thumb.startsWith('/') && apiBase) return `${apiBase.replace(/\/$/, '')}${thumb}`;
  if (thumb.length <= 4 && !thumb.includes('/')) return null;
  return thumb;
};

export const formatCourseDuration = (minutes = 0) => {
  if (!minutes) return '—';
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
};

export const formatCategoryLabel = (category = '') =>
  category
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase()) || 'General';

export const sortEnrollments = (enrollments = [], sortBy = 'recent') => {
  const list = [...enrollments];
  switch (sortBy) {
    case 'progress':
      return list.sort((a, b) => getProgress(b) - getProgress(a));
    case 'title':
      return list.sort((a, b) => (a.course?.title || '').localeCompare(b.course?.title || ''));
    case 'recent':
    default:
      return list.sort((a, b) => {
        const da = new Date(a.progress?.lastAccessedDate || a.enrollmentDate || a.createdAt || 0);
        const db = new Date(b.progress?.lastAccessedDate || b.enrollmentDate || b.createdAt || 0);
        return db - da;
      });
  }
};

export const filterEnrollmentsByStatus = (enrollments = [], status = 'all') => {
  if (status === 'active') return enrollments.filter((e) => e.status !== 'completed');
  if (status === 'completed') return enrollments.filter((e) => e.status === 'completed');
  return enrollments;
};

export const getFocusEnrollment = (enrollments = []) => {
  const active = enrollments.filter((e) => e.status !== 'completed' && e.course?.title);
  if (!active.length) return null;
  return [...active].sort((a, b) => getProgress(b) - getProgress(a))[0];
};

export const getPortalInsight = ({ stats, focusEnrollment, studentName }) => {
  const first = (studentName || 'there').split(' ')[0];
  if (!stats.totalCourses) {
    return {
      tone: 'start',
      headline: `${first}, your workspace is ready`,
      body: 'Enroll in a course and this portal becomes your mission control — progress, labs, AI paths, and certificates in one place.',
      cta: 'Explore courses',
      path: '/courses',
    };
  }
  if (focusEnrollment) {
    const pct = getProgress(focusEnrollment);
    const title = focusEnrollment.course?.title || 'your course';
    if (pct < 25) {
      return {
        tone: 'momentum',
        headline: 'Build the habit early',
        body: `You're ${pct}% into ${title}. One focused session today makes the next lesson feel effortless.`,
        cta: 'Start session',
        path: null,
      };
    }
    if (pct < 75) {
      return {
        tone: 'momentum',
        headline: 'Mid-flight — keep climbing',
        body: `${pct}% through ${title}. You're past the hardest part; finish this module while context is fresh.`,
        cta: 'Continue',
        path: null,
      };
    }
    return {
      tone: 'finish',
      headline: 'The finish line is close',
      body: `${title} is ${pct}% complete. Cross it off and unlock your certificate.`,
      cta: 'Finish course',
      path: null,
    };
  }
  if (stats.coursesCompleted > 0) {
    return {
      tone: 'levelup',
      headline: 'Time to level up',
      body: `You've completed ${stats.coursesCompleted} course${stats.coursesCompleted > 1 ? 's' : ''}. Add a lab or a new specialization to compound your skills.`,
      cta: 'Browse catalog',
      path: '/courses',
    };
  }
  return {
    tone: 'neutral',
    headline: 'Small steps, big compounding',
    body: 'Set a 25-minute block, open your course, and let Qore AI unblock you when stuck.',
    cta: 'Ask Qore AI',
    path: '/aitutoring',
  };
};

export const getWeeklyMomentum = (totalHours = 0) => {
  const goalHours = 5;
  const current = Math.min(totalHours, goalHours);
  const pct = Math.round((current / goalHours) * 100);
  const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  const today = new Date().getDay();
  const mapped = today === 0 ? 6 : today - 1;
  return { goalHours, current, pct, days, todayIndex: mapped };
};

export const buildActivityFeed = (enrollments = [], stats = {}) => {
  const items = [];
  enrollments.slice(0, 4).forEach((e) => {
    const pct = getProgress(e);
    const title = e.course?.title;
    if (!title) return;
    if (e.status === 'completed') {
      items.push({
        text: `Completed ${title}`,
        time: 'Recently',
        color: '#34d399',
        kind: 'complete',
      });
    } else if (pct > 0) {
      items.push({
        text: `${pct}% through ${title}`,
        time: 'In progress',
        color: '#8b5cf6',
        kind: 'progress',
      });
    } else {
      items.push({
        text: `Enrolled in ${title}`,
        time: 'Ready to start',
        color: '#22d3ee',
        kind: 'enroll',
      });
    }
  });
  if (stats.coursesCompleted > 0) {
    items.push({
      text: `${stats.coursesCompleted} course${stats.coursesCompleted > 1 ? 's' : ''} finished — nice velocity`,
      time: 'Milestone',
      color: '#f59e0b',
      kind: 'milestone',
    });
  }
  if (!items.length) {
    items.push({
      text: 'Your activity timeline fills in as you learn',
      time: 'Waiting for you',
      color: '#64748b',
      kind: 'empty',
    });
  }
  return items.slice(0, 5);
};

export const filterEnrollments = (enrollments, query = '') => {
  const q = query.trim().toLowerCase();
  if (!q) return enrollments;
  return enrollments.filter((e) => {
    const title = e.course?.title?.toLowerCase() || '';
    const cat = e.course?.category?.toLowerCase() || '';
    const instructor = e.course?.instructor?.name?.toLowerCase() || '';
    return title.includes(q) || cat.includes(q) || instructor.includes(q);
  });
};

export const getEarnedAchievements = (stats = {}) => {
  const list = [];
  if (stats.totalCourses > 0) {
    list.push({ id: 'enrolled', label: 'First Step', icon: 'book', earned: true });
  }
  if (stats.coursesCompleted > 0) {
    list.push({ id: 'graduate', label: 'Graduate', icon: 'cap', earned: true });
  }
  if (stats.currentStreak >= 7) {
    list.push({ id: 'streak', label: '7-Day Streak', icon: 'flame', earned: true });
  }
  if (stats.achievementPoints >= 1000) {
    list.push({ id: 'star', label: 'Rising Star', icon: 'star', earned: true });
  }
  list.push({ id: 'labs', label: 'Lab Explorer', icon: 'flask', earned: false });
  list.push({ id: 'path', label: 'Path Builder', icon: 'target', earned: false });
  list.push({ id: 'perfect', label: 'Top Scorer', icon: 'medal', earned: false });
  return list;
};